import React, { useEffect, useState } from "react";
import { CircleX } from "lucide-react";
import { User } from "../types/user";
import {
  subscribeDirectory,
  forgetPerson,
  sameNameVenmo,
} from "../data/knownPeople";
import { useAuth } from "../data/AuthContext";
import "./RecentMates.scss";

// The saved-mates directory, kept live from Firestore, plus the people in it
// worth suggesting: not "Me" and not already on this receipt (by id or by
// name+venmo).
export function useKnownPeople(users: User[]) {
  const { uid } = useAuth();
  const [knownPeople, setKnownPeople] = useState<User[]>([]);

  useEffect(() => {
    if (!uid) return;
    return subscribeDirectory(uid, setKnownPeople);
  }, [uid]);

  const selfName = users[0]?.name.trim().toLowerCase() ?? "";
  const suggestions = knownPeople.filter(
    (person) =>
      person.name.trim().toLowerCase() !== selfName &&
      !users.some((u) => u.id === person.id || sameNameVenmo(u, person)),
  );

  return { knownPeople, suggestions };
}

interface RecentMatesProps {
  suggestions: User[];
  onPick: (person: User) => void;
}

// "Recent" chips: tap one to add that person; the x forgets them for good.
const RecentMates: React.FC<RecentMatesProps> = ({ suggestions, onPick }) => {
  const { uid } = useAuth();

  if (suggestions.length === 0) return null;

  return (
    <div className="suggestion-chips">
      {suggestions.map((person) => (
        <div key={person.id} className="suggestion-chip">
          <button className="suggestion-add" onClick={() => onPick(person)}>
            <span className="suggestion-name">{person.name}</span>
            {person.venmo && (
              <span className="suggestion-venmo">@{person.venmo}</span>
            )}
          </button>
          <CircleX
            className="suggestion-forget"
            onClick={() => uid && forgetPerson(uid, person.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default RecentMates;
